import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from './components/DataTable/DataTable';
import { RFQ_COLUMNS, getRFQActionsColumn } from './constants/tableColumns';
import { WORK_TYPES, JOB_STATUSES } from './constants';

/**
 * RFQTableView - Refactored RFQ table using DataTable component
 * 
 * Sprint 20: Replaces 700+ lines of custom filter/sort/table code with DataTable
 */

export default function RFQTableView({
  rfqs,
  cities,
  healthData,
  userRole,
  updateUserStatus,
  updateWorkType,
}) {
  const navigate = useNavigate();

  // Enrich RFQs with city URLs by matching organization names
  const enrichedRfqs = useMemo(() => {
    if (!cities || cities.length === 0) return rfqs;
    
    // Create a map of organization name to city URL for fast lookup
    const cityUrlMap = {};
    cities.forEach(city => {
      if (city.name && city.url) {
        cityUrlMap[city.name] = city.url;
      }
    });
    
    // Add URL to each RFQ by matching organization name
    return rfqs.map(rfq => ({
      ...rfq,
      url: cityUrlMap[rfq.organization] || null
    }));
  }, [rfqs, cities]);

  // Get unique organizations for filter dropdown
  const organizations = useMemo(() => {
    return [...new Set(rfqs.map(rfq => rfq.organization))].sort();
  }, [rfqs]);

  // Prepare table columns with actions
  const columns = useMemo(() => {
    const baseColumns = [...RFQ_COLUMNS];
    const actionsColumn = getRFQActionsColumn(updateUserStatus);
    return [...baseColumns, actionsColumn];
  }, [updateUserStatus]);

  // Filter configuration for RFQ table (memoized to prevent re-renders)
  const filterConfig = useMemo(() => ({
    searchTerm: {
      type: 'text',
      label: 'Search',
      placeholder: 'Search title, number, org...',
      className: 'col-md-3',
    },
    work_type: {
      type: 'select',
      label: 'Work Type',
      options: WORK_TYPES.map(type => ({ value: type, label: type })),
      allLabel: 'All Types',
      className: 'col-md-2',
    },
    user_status: {
      type: 'select',
      label: 'Status',
      options: JOB_STATUSES.map(({ value, label }) => ({ value, label })),
      allLabel: 'All Status',
      className: 'col-md-2',
    },
    organization: {
      type: 'select',
      label: 'Organization',
      options: organizations.map(org => ({ value: org, label: org })),
      allLabel: 'All Organizations',
      className: 'col-md-2',
    },
    source: {
      type: 'select',
      label: 'Source',
      options: [
        { value: 'scraper', label: '🌐 Web Scraper' },
        { value: 'email', label: '📧 Email' },
        { value: 'manual', label: '✏️ Manual Entry' },
      ],
      allLabel: 'All Sources',
      className: 'col-md-2',
    },
    hideIgnored: {
      type: 'checkbox',
      label: 'Hide Irrelevant',
      className: 'col-md-2',
    },
    // Add sort options (second row)
    sortBy: {
      type: 'select',
      label: 'Sort By',
      options: [
        { value: 'due_date_asc', label: 'Due Date (Soonest First) ⏰' },
        { value: 'due_date_desc', label: 'Due Date (Most Time Remaining) ⏳' },
        { value: 'date_added', label: 'Date Added (Newest First) 🆕' },
        { value: 'organization', label: 'Organization (A-Z) 🏛️' },
        { value: 'work_type', label: 'Work Type (A-Z) 🔨' },
      ],
      allLabel: 'Default Sort',
      className: 'col-md-3',
      defaultValue: 'due_date_asc', // Default to soonest first
    },
  }), [organizations]); // Only recreate when organizations change

  // Handle job ID click - navigate to job details
  const handleJobIdClick = (jobId) => {
    navigate(`/job/${jobId}`);
  };

  return (
    <div className="container mt-4">
      {/* Health Alerts - Dev Only */}
      {userRole === 'dev' && healthData && healthData.alerts && healthData.alerts.length > 0 && (
        <div className="alert-container mb-4">
          <h6 className="text-muted small mb-2">Developer Information:</h6>
          {healthData.has_critical_alerts && (
            <div className="alert alert-danger">
              <h5 className="alert-heading">🚨 Critical Issues Detected</h5>
              <ul className="mb-0">
                {healthData.alerts.filter(a => a.severity === 'critical').map((alert, idx) => (
                  <li key={idx}><strong>{alert.city}:</strong> {alert.message}</li>
                ))}
              </ul>
            </div>
          )}
          {healthData.has_warnings && !healthData.has_critical_alerts && (
            <div className="alert alert-warning">
              <h5 className="alert-heading">⚠️ Scraper Warnings</h5>
              <ul className="mb-0">
                {healthData.alerts.filter(a => a.severity === 'warning' || a.severity === 'error').map((alert, idx) => (
                  <li key={idx}><strong>{alert.city}:</strong> {alert.message}</li>
                ))}
              </ul>
            </div>
          )}
          {healthData.timestamp && (
            <div className="text-muted small">
              Last scrape: {new Date(healthData.timestamp).toLocaleString()} | 
              Cities: {healthData.cities_scraped} | 
              Success: {healthData.success_count} | 
              Failed: {healthData.failed_count}
            </div>
          )}
        </div>
      )}

      {/* Sprint 20: Refactored Table with DataTable Component */}
      <div className="card">
        <div className="card-body">
          <h5 className="card-title mb-4">RFQs</h5>
          
          <DataTable
            columns={columns}
            data={enrichedRfqs}
            tableId="rfq-table"
            enableRowSelection={false}
            enableColumnResizing={true}
            enableColumnOrdering={true}
            enableFilters={true}
            enablePagination={true}
            pageSize={50}
            filterConfig={filterConfig}
            meta={{
              // Note: NOT passing updateUserStatus so Status shows as badges (not dropdown)
              updateWorkType,
              onJobIdClick: handleJobIdClick,
            }}
          />
        </div>
      </div>
    </div>
  );
}

